import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaShieldAlt,
  FaIdCard,
  FaFingerprint,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import FinoDmtService from "../../../services/FinoDmtService";
import BiometricService from "../../../services/BiometricService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";
import BiometricModal from "../../../components/Dialogs/BiometricModal"; // adjust path

// Hardcoded WADH – replace with actual value if needed
const WADH_VALUE = "18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=";

const FinoEkyc = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);
  const [aadhaarConsent, setAadhaarConsent] = useState("");
  const [formData, setFormData] = useState({
    aadhaar_number: "",
    dob: "",
    aadhaar_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);

  // Location state
  const [location, setLocation] = useState({
    lat: null,
    lng: null,
    error: null,
    loading: false,
  });

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      setLocation((prev) => ({ ...prev, loading: true, error: null }));

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({
            lat: latitude,
            lng: longitude,
            error: null,
            loading: false,
          });
          resolve({ lat: latitude, lng: longitude });
        },
        (error) => {
          let errorMessage = "Unable to retrieve your location.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Location permission denied. Please enable location access in your browser settings.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable.";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out.";
              break;
            default:
              errorMessage = "An unknown error occurred.";
          }
          setLocation({
            lat: null,
            lng: null,
            error: errorMessage,
            loading: false,
          });
          reject(new Error(errorMessage));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  };

  // Handle form input changes (same as before)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;

    if (name === "aadhaar_number") {
      formattedValue = value.replace(/\D/g, "").substring(0, 12);
      if (formattedValue.length > 8) {
        formattedValue = formattedValue.replace(
          /(\d{4})(\d{4})(\d{0,4})/,
          "$1 $2 $3",
        );
      } else if (formattedValue.length > 4) {
        formattedValue = formattedValue.replace(/(\d{4})(\d{0,4})/, "$1 $2");
      }
    } else if (name === "dob") {
      let cleaned = value.replace(/\D/g, "");
      if (cleaned.length >= 2 && cleaned.length < 4) {
        cleaned = cleaned.replace(/^(\d{2})/, "$1/");
      } else if (cleaned.length >= 4 && cleaned.length < 6) {
        cleaned = cleaned.replace(/^(\d{2})(\d{2})/, "$1/$2/");
      } else if (cleaned.length >= 6) {
        cleaned = cleaned.replace(/^(\d{2})(\d{2})(\d{0,4})/, "$1/$2/$3");
      }
      formattedValue = cleaned.substring(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form (unchanged)
  const validateForm = () => {
    const newErrors = {};
    const cleanAadhaar = formData.aadhaar_number.replace(/\s/g, "");

    if (!cleanAadhaar.trim()) {
      newErrors.aadhaar_number = "Aadhaar number is required";
    } else if (cleanAadhaar.length !== 12) {
      newErrors.aadhaar_number = "Aadhaar number must be 12 digits";
    } else if (!/^\d{12}$/.test(cleanAadhaar)) {
      newErrors.aadhaar_number = "Invalid Aadhaar number format";
    }

    const dobRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!formData.dob.trim()) {
      newErrors.dob = "Date of birth is required";
    } else if (!dobRegex.test(formData.dob)) {
      newErrors.dob = "Invalid date format. Use DD/MM/YYYY";
    } else {
      const day = parseInt(formData.dob.split("/")[0], 10);
      const month = parseInt(formData.dob.split("/")[1], 10);
      const year = parseInt(formData.dob.split("/")[2], 10);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        year < 1900 ||
        year > new Date().getFullYear()
      ) {
        newErrors.dob = "Invalid date of birth";
      } else {
        const today = new Date();
        let age = today.getFullYear() - year;
        const m = today.getMonth() - month;
        if (m < 0 || (m === 0 && today.getDate() < day)) age--;
        if (age < 18) {
          newErrors.dob = "You must be at least 18 years old";
        }
      }
    }

    if (!formData.aadhaar_consent) {
      newErrors.aadhaar_consent = "You must agree to the Aadhaar consent";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle "Verify Aadhaar" click – get location first, then open biometric modal
  const handleVerifyAadhaar = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix all errors before verifying",
        timer: 2000,
      });
      return;
    }

    // Get location first
    try {
      await getCurrentLocation();
      // Location obtained – open biometric modal
      setShowBiometricModal(true);
    } catch (locError) {
      Swal.fire({
        icon: "error",
        title: "Location Required",
        html: `${locError.message}<br/><br/>Please enable location services and try again.`,
        confirmButtonText: "Retry",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          handleVerifyAadhaar(e); // retry
        }
      });
    }
  };

  // Called when biometric capture succeeds
  const handleBiometricSuccess = async (captureData) => {
    setLoading(true);
    try {
      // Ensure we have location (should already be obtained)
      let lat = location.lat;
      let lng = location.lng;
      if (!lat || !lng) {
        // Fallback – try one more time
        const coords = await getCurrentLocation();
        lat = coords.lat;
        lng = coords.lng;
      }

      const xmlString = BiometricService.convertXmlToBase64(
        captureData.xmlResponse,
      );

      console.log(xmlString);
      console.log(captureData.xmlResponse);
      const aadhaar_number = formData.aadhaar_number.replace(/\s/g, "");
      const dob = formData.dob;

      const result = await FinoDmtService.finoEkyc(
        aadhaar_number,
        dob,
        xmlString,
        lat.toString(),
        lng.toString(),
      );

      if (!result.success) {
        throw new Error(result.message || "Biometric verification failed");
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "E-KYC Successful",
          text: res.message || "Merchant E-KYC Successful",
        }).then(() => {
          navigate("/bc-agent-onboarding");
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Biometric Verification Failed",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/bc-agent-onboarding");
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Fino Payments Bank E-KYC</h5>
                <Button
                  variant="outline-secondary"
                  onClick={handleGoBack}
                  size="sm"
                >
                  <FaArrowLeft className="me-1" />
                  Back
                </Button>
              </div>

              <Form onSubmit={handleVerifyAadhaar}>
                {/* Aadhaar Number Input */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>
                      <FaUser className="me-2" />
                      Aadhaar Number
                    </strong>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaIdCard />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="aadhaar_number"
                      placeholder="Enter 12-digit Aadhaar number"
                      value={formData.aadhaar_number}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_number}
                      maxLength="14"
                    />
                  </InputGroup>
                  {errors.aadhaar_number && (
                    <Form.Text className="text-danger">
                      {errors.aadhaar_number}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Enter your 12-digit Aadhaar number (e.g., 1234 5678 9012)
                  </Form.Text>
                </Form.Group>

                {/* Date of Birth Input */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>
                      <FaCalendarAlt className="me-2" />
                      Date of Birth
                    </strong>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaCalendarAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="dob"
                      placeholder="DD/MM/YYYY"
                      value={formData.dob}
                      onChange={handleChange}
                      isInvalid={!!errors.dob}
                      maxLength="10"
                    />
                  </InputGroup>
                  {errors.dob && (
                    <Form.Text className="text-danger">{errors.dob}</Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Enter your date of birth in DD/MM/YYYY format
                  </Form.Text>
                </Form.Group>

                {/* Location Status */}
                {/* <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>
                      <FaMapMarkerAlt className="me-2" />
                      Location
                    </strong>
                  </Form.Label>
                  <div className="border rounded p-3 bg-light">
                    {location.loading ? (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="me-2" />
                        <span>Fetching location...</span>
                      </div>
                    ) : location.error ? (
                      <div className="d-flex align-items-center text-danger">
                        <FaExclamationTriangle className="me-2" />
                        <span>{location.error}</span>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => getCurrentLocation()}
                          className="ms-2 p-0"
                        >
                          Retry
                        </Button>
                      </div>
                    ) : location.lat && location.lng ? (
                      <div className="d-flex align-items-center text-success">
                        <FaCheckCircle className="me-2" />
                        <span>
                          Location enabled: {location.lat.toFixed(6)},{" "}
                          {location.lng.toFixed(6)}
                        </span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center text-muted">
                        <FaMapMarkerAlt className="me-2" />
                        <span>Location will be fetched on verification</span>
                      </div>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Location is required for Aadhaar verification. Ensure
                    location services are enabled.
                  </Form.Text>
                </Form.Group> */}

                {/* Aadhaar Consent Checkbox */}
                <Form.Group className="mb-4">
                  <div className="border rounded-3 p-3 bg-light">
                    <Form.Check
                      type="checkbox"
                      id="aadhaar-consent"
                      label={
                        <span>
                          I hereby give my consent for Aadhaar verification as
                          per the{" "}
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none btn-sm fw-bold"
                            onClick={() => setShowConsentModal(true)}
                          >
                            Aadhaar Consent
                          </Button>
                        </span>
                      }
                      name="aadhaar_consent"
                      checked={formData.aadhaar_consent}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_consent}
                    />
                    {errors.aadhaar_consent && (
                      <Alert variant="danger" className="mt-2 py-2">
                        <FaExclamationTriangle className="me-2" />
                        {errors.aadhaar_consent}
                      </Alert>
                    )}
                  </div>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="py-3 fw-bold rounded-3 shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaFingerprint className="me-2" />
                        Capture Fingerprint
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Row>

      {/* Aadhaar Consent Modal (unchanged) */}
      <Modal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2" />
            Aadhaar Consent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="border p-3 bg-light rounded">
            <h6 className="fw-bold mb-2">Aadhaar Consent Declaration</h6>

            <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
              I hereby give my consent and have no objection to using my Aadhaar
              details for the purpose of identity verification (E-KYC). I
              voluntarily authorize the service provider to access and use my
              Aadhaar information as per UIDAI guidelines for authentication. I
              understand that my information will be used only for verification
              purposes and will not be misused or shared without my consent.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConsentModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setFormData((prev) => ({ ...prev, aadhaar_consent: true }));
              setShowConsentModal(false);
            }}
          >
            <FaCheckCircle className="me-1" />I Agree
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Biometric Modal */}
      <BiometricModal
        open={showBiometricModal}
        onClose={() => setShowBiometricModal(false)}
        onSuccess={handleBiometricSuccess}
        aadhaarNumber={formData.aadhaar_number.replace(/\s/g, "")}
        wadh={WADH_VALUE}
      />
    </Container>
  );
};

export default FinoEkyc;
